import {
  useMemo,
  useState,
} from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  DeleteRounded,
  EditRounded,
  FactCheckRounded,
  LinkRounded,
  TuneRounded,
  WorkspacesRounded,
} from '@mui/icons-material';
import {
  createIndustrialWorkflowWorkReportDefinition,
  createIndustrialWorkflowWorkReportFieldDefinition,
  deleteIndustrialWorkflowWorkReportDefinition,
  deleteIndustrialWorkflowWorkReportFieldDefinition,
  listIndustrialWorkflowFields,
  listIndustrialWorkflowWorkAreas,
  listIndustrialWorkflowWorkReportDefinitions,
  listIndustrialWorkflowWorkReportFieldDefinitions,
  updateIndustrialWorkflowWorkReportDefinition,
  updateIndustrialWorkflowWorkReportFieldDefinition,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowFieldDefinition,
  IndustrialWorkflowFieldType,
  IndustrialWorkflowWorkReportContinuityMode,
  IndustrialWorkflowWorkReportCreationMode,
  IndustrialWorkflowWorkReportDefinition,
  IndustrialWorkflowWorkReportDefinitionUpsertPayload,
  IndustrialWorkflowWorkReportFieldDefinition,
  IndustrialWorkflowWorkReportFieldDefinitionUpsertPayload,
  IndustrialWorkflowWorkReportFieldSource,
} from '../types/industrialWorkflows';

type Props = {
  processDefinitionId: string;
  stageDefinitionId: string;
};

type WorkReportCaseFieldWriteMode =
  | 'read_only'
  | 'on_complete';

type WorkReportDefinitionFormState = {
  code: string;
  name: string;
  description: string;
  work_area_id: string;
  creation_mode: IndustrialWorkflowWorkReportCreationMode;
  is_required_to_complete_stage: boolean;
  continuity_mode: IndustrialWorkflowWorkReportContinuityMode;
  display_order: number;
  is_active: boolean;
  settings: Record<string, unknown>;
};

type WorkReportFieldFormState = {
  field_source: IndustrialWorkflowWorkReportFieldSource;
  case_field_definition_id: string;
  case_field_write_mode: WorkReportCaseFieldWriteMode;
  code: string;
  label: string;
  description: string;
  field_type: IndustrialWorkflowFieldType;
  display_order: number;
  is_required_to_complete: boolean;
  is_readonly: boolean;
  is_active: boolean;
  options_text: string;
  settings_text: string;
};

const EMPTY_DEFINITION_FORM: WorkReportDefinitionFormState = {
  code: '',
  name: '',
  description: '',
  work_area_id: '',
  creation_mode: 'auto_on_stage_entry',
  is_required_to_complete_stage: true,
  continuity_mode: 'always_new',
  display_order: 0,
  is_active: true,
  settings: {},
};

const EMPTY_FIELD_FORM: WorkReportFieldFormState = {
  field_source: 'work_report_field',
  case_field_definition_id: '',
  case_field_write_mode: 'read_only',
  code: '',
  label: '',
  description: '',
  field_type: 'text',
  display_order: 0,
  is_required_to_complete: false,
  is_readonly: false,
  is_active: true,
  options_text: '',
  settings_text: '{}',
};

const FIELD_TYPES: Array<{
  value: IndustrialWorkflowFieldType;
  label: string;
}> = [
  {
    value: 'text',
    label: 'Texto corto',
  },
  {
    value: 'long_text',
    label: 'Texto largo',
  },
  {
    value: 'integer',
    label: 'Número entero',
  },
  {
    value: 'number',
    label: 'Número decimal',
  },
  {
    value: 'currency',
    label: 'Importe',
  },
  {
    value: 'boolean',
    label: 'Sí / no',
  },
  {
    value: 'date',
    label: 'Fecha',
  },
  {
    value: 'datetime',
    label: 'Fecha y hora',
  },
  {
    value: 'select',
    label: 'Lista de opciones',
  },
  {
    value: 'multi_select',
    label: 'Selección múltiple',
  },
  {
    value: 'user',
    label: 'Usuario',
  },
  {
    value: 'team',
    label: 'Equipo',
  },
  {
    value: 'email',
    label: 'Correo electrónico',
  },
  {
    value: 'url',
    label: 'Enlace',
  },
];

const WORK_REPORT_DIALOG_BACKDROP_PROPS = {
  sx: {
    bgcolor: 'rgba(2,6,23,0.78)',
    backdropFilter: 'blur(6px)',
  },
};

const WORK_REPORT_DIALOG_PAPER_PROPS = {
  sx: {
    borderRadius: 4,
    border: `1px solid ${alpha('#38bdf8', 0.22)}`,
    bgcolor: '#0f1b2f',
    backgroundImage: 'none',
    boxShadow: '0 28px 80px rgba(0,0,0,0.48)',
    overflow: 'hidden',
  },
};

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function normalizeCode(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(
      /[\u0300-\u036f]/g,
      '',
    )
    .replace(
      /[^a-zA-Z0-9]+/g,
      '_',
    )
    .replace(
      /^_+|_+$/g,
      '',
    )
    .toUpperCase();
}

function getNextDefinitionDisplayOrder(
  definitions: IndustrialWorkflowWorkReportDefinition[],
) {
  if (!definitions.length) {
    return 10;
  }

  return (
    Math.max(
      ...definitions.map(
        (definition) =>
          definition.display_order,
      ),
    )
    + 10
  );
}

function getNextFieldDisplayOrder(
  fields: IndustrialWorkflowWorkReportFieldDefinition[],
) {
  if (!fields.length) {
    return 10;
  }

  return (
    Math.max(
      ...fields.map(
        (field) =>
          field.display_order,
      ),
    )
    + 10
  );
}

function parseOptions(value: string) {
  return Array.from(
    new Set(
      value
        .split('\n')
        .map((option) => option.trim())
        .filter(Boolean),
    ),
  );
}

function parseSettings(value: string) {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return {};
  }

  const parsedValue = JSON.parse(normalizedValue);

  if (
    typeof parsedValue !== 'object'
    || parsedValue === null
    || Array.isArray(parsedValue)
  ) {
    throw new Error(
      'La configuración adicional debe ser un objeto JSON.',
    );
  }

  return parsedValue as Record<string, unknown>;
}

function getCaseFieldWriteMode(
  settings: Record<string, unknown> | null | undefined,
): WorkReportCaseFieldWriteMode {
  return settings?.case_field_write_mode === 'on_complete'
    ? 'on_complete'
    : 'read_only';
}

function getFieldTypeLabel(fieldType: IndustrialWorkflowFieldType) {
  return (
    FIELD_TYPES.find(
      (option) => option.value === fieldType,
    )?.label ?? fieldType
  );
}

function getCreationModeLabel(
  creationMode: IndustrialWorkflowWorkReportCreationMode,
) {
  if (creationMode === 'manual') {
    return 'Manual';
  }

  return 'Al entrar en etapa';
}

function getContinuityModeLabel(
  continuityMode: IndustrialWorkflowWorkReportContinuityMode,
) {
  if (continuityMode === 'ask_on_reentry') {
    return 'Preguntar al volver';
  }

  if (continuityMode === 'continue_open_previous') {
    return 'Continuar abierto';
  }

  return 'Siempre nuevo';
}

function buildDefinitionPayload(
  form: WorkReportDefinitionFormState,
): IndustrialWorkflowWorkReportDefinitionUpsertPayload {
  return {
    code: form.code.trim() || null,
    name: form.name.trim(),
    description: form.description.trim() || null,
    work_area_id: form.work_area_id || null,
    creation_mode: form.creation_mode,
    is_required_to_complete_stage:
      form.is_required_to_complete_stage,
    continuity_mode: form.continuity_mode,
    display_order: form.display_order,
    is_active: form.is_active,
    settings: form.settings,
  };
}

function buildDefinitionForm(
  definition: IndustrialWorkflowWorkReportDefinition,
): WorkReportDefinitionFormState {
  return {
    code: definition.code,
    name: definition.name,
    description: definition.description ?? '',
    work_area_id: definition.work_area_id ?? '',
    creation_mode: definition.creation_mode,
    is_required_to_complete_stage:
      definition.is_required_to_complete_stage,
    continuity_mode: definition.continuity_mode,
    display_order: definition.display_order,
    is_active: definition.is_active,
    settings: definition.settings ?? {},
  };
}

function buildFieldForm(
  field: IndustrialWorkflowWorkReportFieldDefinition,
): WorkReportFieldFormState {
  return {
    field_source: field.field_source,
    case_field_definition_id:
      field.case_field_definition_id ?? '',
    case_field_write_mode:
      getCaseFieldWriteMode(field.settings),
    code: field.code,
    label: field.label,
    description: field.description ?? '',
    field_type: field.field_type,
    display_order: field.display_order,
    is_required_to_complete:
      field.is_required_to_complete,
    is_readonly: field.is_readonly,
    is_active: field.is_active,
    options_text: (field.options ?? [])
      .map((option) =>
        typeof option === 'string'
          ? option
          : JSON.stringify(option),
      )
      .join('\n'),
    settings_text: JSON.stringify(
      field.settings ?? {},
      null,
      2,
    ),
  };
}

function buildFieldPayload(
  form: WorkReportFieldFormState,
  selectedCaseField: IndustrialWorkflowFieldDefinition | undefined,
): IndustrialWorkflowWorkReportFieldDefinitionUpsertPayload {
  const fieldSource = form.field_source;

  const fieldType =
    fieldSource === 'case_field' && selectedCaseField
      ? selectedCaseField.field_type
      : form.field_type;

  const fieldUsesOptions =
    fieldType === 'select'
    || fieldType === 'multi_select';

  const parsedSettings =
    parseSettings(form.settings_text);

  const settings =
    fieldSource === 'case_field'
      ? {
          ...parsedSettings,
          case_field_write_mode:
            form.case_field_write_mode,
        }
      : parsedSettings;

  return {
    field_source: fieldSource,
    case_field_definition_id:
      fieldSource === 'case_field'
        ? form.case_field_definition_id || null
        : null,
    code: form.code.trim() || null,
    label:
      form.label.trim()
      || selectedCaseField?.label
      || '',
    description:
      form.description.trim()
      || selectedCaseField?.description
      || null,
    field_type: fieldType,
    display_order: form.display_order,
    is_required_to_complete:
      form.is_required_to_complete,
    is_readonly:
      fieldSource === 'case_field'
        ? form.case_field_write_mode !== 'on_complete'
        : form.is_readonly,
    is_active: form.is_active,
    options:
      fieldSource === 'case_field'
        ? selectedCaseField?.options ?? []
        : fieldUsesOptions
          ? parseOptions(form.options_text)
          : [],
    settings,
  };
}

export default function IndustrialWorkflowsStageWorkReportsConfigSection({
  processDefinitionId,
  stageDefinitionId,
}: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [
    definitionDialogOpen,
    setDefinitionDialogOpen,
  ] = useState(false);

  const [
    fieldDialogOpen,
    setFieldDialogOpen,
  ] = useState(false);

  const [
    editingDefinition,
    setEditingDefinition,
  ] =
    useState<IndustrialWorkflowWorkReportDefinition | null>(
      null,
    );

  const [
    selectedDefinition,
    setSelectedDefinition,
  ] =
    useState<IndustrialWorkflowWorkReportDefinition | null>(
      null,
    );

  const [
    definitionPendingDelete,
    setDefinitionPendingDelete,
  ] =
    useState<IndustrialWorkflowWorkReportDefinition | null>(
      null,
    );

  const [
    editingField,
    setEditingField,
  ] =
    useState<IndustrialWorkflowWorkReportFieldDefinition | null>(
      null,
    );

  const [
    fieldPendingDelete,
    setFieldPendingDelete,
  ] =
    useState<IndustrialWorkflowWorkReportFieldDefinition | null>(
      null,
    );

  const [
    definitionForm,
    setDefinitionForm,
  ] = useState<WorkReportDefinitionFormState>(
    EMPTY_DEFINITION_FORM,
  );

  const [
    fieldForm,
    setFieldForm,
  ] = useState<WorkReportFieldFormState>(
    EMPTY_FIELD_FORM,
  );

  const definitionsQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-report-definitions',
      processDefinitionId,
      stageDefinitionId,
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkReportDefinitions(
        processDefinitionId,
        stageDefinitionId,
        true,
      ),
    retry: false,
  });

  const fieldsQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-report-fields',
      processDefinitionId,
      stageDefinitionId,
      selectedDefinition?.id,
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkReportFieldDefinitions(
        processDefinitionId,
        stageDefinitionId,
        selectedDefinition?.id as string,
        true,
      ),
    enabled: Boolean(selectedDefinition),
    retry: false,
  });

  const workAreasQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-areas',
      false,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkAreas(false),
    retry: false,
  });

  const caseFieldsQuery = useQuery({
    queryKey: [
      'industrial-workflows-fields',
      processDefinitionId,
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowFields(
        processDefinitionId,
        true,
      ),
    retry: false,
  });

  const orderedDefinitions = useMemo(
    () =>
      [...(definitionsQuery.data ?? [])].sort(
        (left, right) =>
          left.display_order - right.display_order
          || left.name.localeCompare(right.name),
      ),
    [definitionsQuery.data],
  );

  const orderedFields = useMemo(
    () =>
      [...(fieldsQuery.data ?? [])].sort(
        (left, right) =>
          left.display_order - right.display_order
          || left.label.localeCompare(right.label),
      ),
    [fieldsQuery.data],
  );

  const workAreaNameById = useMemo(
    () =>
      new Map(
        (workAreasQuery.data ?? []).map(
          (area) => [
            area.id,
            area.name,
          ],
        ),
      ),
    [workAreasQuery.data],
  );

  const caseFieldById = useMemo(
    () =>
      new Map(
        (caseFieldsQuery.data ?? []).map(
          (field) => [
            field.id,
            field,
          ],
        ),
      ),
    [caseFieldsQuery.data],
  );

  const selectedCaseField =
    fieldForm.case_field_definition_id
      ? caseFieldById.get(
          fieldForm.case_field_definition_id,
        )
      : undefined;

  const invalidateDefinitions = async () => {
    await queryClient.invalidateQueries({
      queryKey: [
        'industrial-workflows-work-report-definitions',
        processDefinitionId,
        stageDefinitionId,
      ],
    });
  };

  const invalidateFields = async () => {
    if (!selectedDefinition) {
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: [
        'industrial-workflows-work-report-fields',
        processDefinitionId,
        stageDefinitionId,
        selectedDefinition.id,
      ],
    });
  };

  const createDefinitionMutation = useMutation({
    mutationFn: (
      payload: IndustrialWorkflowWorkReportDefinitionUpsertPayload,
    ) =>
      createIndustrialWorkflowWorkReportDefinition(
        processDefinitionId,
        stageDefinitionId,
        payload,
      ),
    onSuccess: async (createdDefinition) => {
      await invalidateDefinitions();

      setDefinitionDialogOpen(false);
      setEditingDefinition(null);
      setDefinitionForm(EMPTY_DEFINITION_FORM);
      setSelectedDefinition(createdDefinition);

      enqueueSnackbar(
        'Parte de trabajo configurado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const updateDefinitionMutation = useMutation({
    mutationFn: ({
      workReportDefinitionId,
      payload,
    }: {
      workReportDefinitionId: string;
      payload: IndustrialWorkflowWorkReportDefinitionUpsertPayload;
    }) =>
      updateIndustrialWorkflowWorkReportDefinition(
        processDefinitionId,
        stageDefinitionId,
        workReportDefinitionId,
        payload,
      ),
    onSuccess: async (updatedDefinition) => {
      await invalidateDefinitions();

      setDefinitionDialogOpen(false);
      setEditingDefinition(null);
      setDefinitionForm(EMPTY_DEFINITION_FORM);
      setSelectedDefinition(updatedDefinition);

      enqueueSnackbar(
        'Parte de trabajo actualizado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const deleteDefinitionMutation = useMutation({
    mutationFn: (workReportDefinitionId: string) =>
      deleteIndustrialWorkflowWorkReportDefinition(
        processDefinitionId,
        stageDefinitionId,
        workReportDefinitionId,
      ),
    onSuccess: async (_, deletedDefinitionId) => {
      await invalidateDefinitions();

      if (selectedDefinition?.id === deletedDefinitionId) {
        setSelectedDefinition(null);
      }

      setDefinitionPendingDelete(null);
      setDefinitionDialogOpen(false);
      setEditingDefinition(null);
      setDefinitionForm(EMPTY_DEFINITION_FORM);

      enqueueSnackbar(
        'Parte de trabajo eliminado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const createFieldMutation = useMutation({
    mutationFn: ({
      workReportDefinitionId,
      payload,
    }: {
      workReportDefinitionId: string;
      payload: IndustrialWorkflowWorkReportFieldDefinitionUpsertPayload;
    }) =>
      createIndustrialWorkflowWorkReportFieldDefinition(
        processDefinitionId,
        stageDefinitionId,
        workReportDefinitionId,
        payload,
      ),
    onSuccess: async () => {
      await invalidateFields();

      setFieldDialogOpen(false);
      setEditingField(null);
      setFieldForm(EMPTY_FIELD_FORM);

      enqueueSnackbar(
        'Campo de parte creado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const updateFieldMutation = useMutation({
    mutationFn: ({
      workReportDefinitionId,
      fieldDefinitionId,
      payload,
    }: {
      workReportDefinitionId: string;
      fieldDefinitionId: string;
      payload: IndustrialWorkflowWorkReportFieldDefinitionUpsertPayload;
    }) =>
      updateIndustrialWorkflowWorkReportFieldDefinition(
        processDefinitionId,
        stageDefinitionId,
        workReportDefinitionId,
        fieldDefinitionId,
        payload,
      ),
    onSuccess: async () => {
      await invalidateFields();

      setFieldDialogOpen(false);
      setEditingField(null);
      setFieldForm(EMPTY_FIELD_FORM);

      enqueueSnackbar(
        'Campo de parte actualizado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const deleteFieldMutation = useMutation({
    mutationFn: ({
      workReportDefinitionId,
      fieldDefinitionId,
    }: {
      workReportDefinitionId: string;
      fieldDefinitionId: string;
    }) =>
      deleteIndustrialWorkflowWorkReportFieldDefinition(
        processDefinitionId,
        stageDefinitionId,
        workReportDefinitionId,
        fieldDefinitionId,
      ),
    onSuccess: async () => {
      await invalidateFields();

      setFieldPendingDelete(null);
      setFieldDialogOpen(false);
      setEditingField(null);
      setFieldForm(EMPTY_FIELD_FORM);

      enqueueSnackbar(
        'Campo de parte eliminado correctamente.',
        {
          variant: 'success',
        },
      );
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const definitionMutationIsPending =
    createDefinitionMutation.isPending
    || updateDefinitionMutation.isPending
    || deleteDefinitionMutation.isPending;

  const fieldMutationIsPending =
    createFieldMutation.isPending
    || updateFieldMutation.isPending
    || deleteFieldMutation.isPending;

  const handleCreateDefinition = () => {
    setEditingDefinition(null);
    setDefinitionForm({
      ...EMPTY_DEFINITION_FORM,
      display_order: getNextDefinitionDisplayOrder(
        orderedDefinitions,
      ),
    });
    setDefinitionDialogOpen(true);
  };

  const handleEditDefinition = (
    definition: IndustrialWorkflowWorkReportDefinition,
  ) => {
    setEditingDefinition(definition);
    setDefinitionForm(buildDefinitionForm(definition));
    setDefinitionDialogOpen(true);
  };

  const handleSaveDefinition = () => {
    if (!definitionForm.name.trim()) {
      enqueueSnackbar(
        'Indica el nombre del parte de trabajo.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    const payload =
      buildDefinitionPayload(definitionForm);

    if (editingDefinition) {
      updateDefinitionMutation.mutate({
        workReportDefinitionId:
          editingDefinition.id,
        payload,
      });

      return;
    }

    createDefinitionMutation.mutate(payload);
  };

  const handleCreateOwnField = () => {
    if (!selectedDefinition) {
      return;
    }

    setEditingField(null);
    setFieldForm({
      ...EMPTY_FIELD_FORM,
      field_source: 'work_report_field',
      display_order:
        getNextFieldDisplayOrder(orderedFields),
    });
    setFieldDialogOpen(true);
  };

  const handleCreateCaseField = () => {
    if (!selectedDefinition) {
      return;
    }

    setEditingField(null);
    setFieldForm({
      ...EMPTY_FIELD_FORM,
      field_source: 'case_field',
      case_field_write_mode: 'read_only',
      is_readonly: true,
      settings_text: JSON.stringify(
        {
          case_field_write_mode: 'read_only',
        },
        null,
        2,
      ),
      display_order:
        getNextFieldDisplayOrder(orderedFields),
    });
    setFieldDialogOpen(true);
  };

  const handleEditField = (
    field: IndustrialWorkflowWorkReportFieldDefinition,
  ) => {
    setEditingField(field);
    setFieldForm(buildFieldForm(field));
    setFieldDialogOpen(true);
  };

  const handleSaveField = () => {
    if (!selectedDefinition) {
      return;
    }

    const normalizedLabel =
      fieldForm.label.trim()
      || selectedCaseField?.label
      || '';

    if (!normalizedLabel) {
      enqueueSnackbar(
        'Indica el nombre del campo.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    if (
      fieldForm.field_source === 'case_field'
      && !fieldForm.case_field_definition_id
    ) {
      enqueueSnackbar(
        'Selecciona el campo del expediente.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    const fieldType =
      fieldForm.field_source === 'case_field'
        ? selectedCaseField?.field_type
        : fieldForm.field_type;

    const fieldUsesOptions =
      fieldType === 'select'
      || fieldType === 'multi_select';

    if (
      fieldForm.field_source === 'work_report_field'
      && fieldUsesOptions
      && !parseOptions(fieldForm.options_text).length
    ) {
      enqueueSnackbar(
        'Introduce al menos una opción disponible.',
        {
          variant: 'warning',
        },
      );

      return;
    }

    try {
      const normalizedForm = {
        ...fieldForm,
        label: normalizedLabel,
        code:
          fieldForm.code
          || normalizeCode(normalizedLabel),
      };

      const payload = buildFieldPayload(
        normalizedForm,
        selectedCaseField,
      );

      if (editingField) {
        updateFieldMutation.mutate({
          workReportDefinitionId:
            selectedDefinition.id,
          fieldDefinitionId: editingField.id,
          payload,
        });

        return;
      }

      createFieldMutation.mutate({
        workReportDefinitionId:
          selectedDefinition.id,
        payload,
      });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    }
  };

  const handleCloseDefinitionDialog = () => {
    if (definitionMutationIsPending) {
      return;
    }

    setDefinitionDialogOpen(false);
    setEditingDefinition(null);
    setDefinitionForm(EMPTY_DEFINITION_FORM);
  };

  const handleCloseFieldDialog = () => {
    if (fieldMutationIsPending) {
      return;
    }

    setFieldDialogOpen(false);
    setEditingField(null);
    setFieldForm(EMPTY_FIELD_FORM);
  };

  const handleConfirmDeleteDefinition = () => {
    if (!definitionPendingDelete) {
      return;
    }

    deleteDefinitionMutation.mutate(
      definitionPendingDelete.id,
    );
  };

  const handleConfirmDeleteField = () => {
    if (
      !selectedDefinition
      || !fieldPendingDelete
    ) {
      return;
    }

    deleteFieldMutation.mutate({
      workReportDefinitionId:
        selectedDefinition.id,
      fieldDefinitionId: fieldPendingDelete.id,
    });
  };

  const renderDefinition = (
    definition: IndustrialWorkflowWorkReportDefinition,
  ) => {
    const isSelected =
      selectedDefinition?.id === definition.id;

    return (
      <Paper
        key={definition.id}
        elevation={0}
        onClick={() =>
          setSelectedDefinition(definition)
        }
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(
            isSelected
              ? '#38bdf8'
              : definition.is_active
                ? '#a78bfa'
                : '#64748b',
            isSelected ? 0.44 : 0.18,
          )}`,
          bgcolor: isSelected
            ? alpha('#0ea5e9', 0.14)
            : 'rgba(2,6,23,0.22)',
          px: 1.35,
          py: 1.15,
          cursor: 'pointer',
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={0.9}
            alignItems="flex-start"
          >
            <FactCheckRounded
              sx={{
                mt: 0.1,
                color: definition.is_active
                  ? '#c4b5fd'
                  : '#64748b',
              }}
            />

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {definition.name}
              </Typography>

              {definition.description ? (
                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.8rem',
                    mt: 0.25,
                  }}
                >
                  {definition.description}
                </Typography>
              ) : null}

              <Stack
                direction="row"
                spacing={0.55}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  mt: 0.7,
                }}
              >
                <Chip
                  size="small"
                  label={getCreationModeLabel(
                    definition.creation_mode,
                  )}
                  sx={{
                    color: '#bfdbfe',
                    bgcolor: alpha(
                      '#3b82f6',
                      0.15,
                    ),
                  }}
                />

                <Chip
                  size="small"
                  label={
                    definition.is_required_to_complete_stage
                      ? 'Obligatorio para avanzar'
                      : 'Opcional'
                  }
                  sx={{
                    color:
                      definition.is_required_to_complete_stage
                        ? '#fde68a'
                        : '#cbd5e1',
                    bgcolor:
                      definition.is_required_to_complete_stage
                        ? alpha(
                            '#f59e0b',
                            0.15,
                          )
                        : alpha(
                            '#64748b',
                            0.12,
                          ),
                  }}
                />

                <Chip
                  size="small"
                  label={getContinuityModeLabel(
                    definition.continuity_mode,
                  )}
                  sx={{
                    color: '#cbd5e1',
                    bgcolor: alpha(
                      '#64748b',
                      0.12,
                    ),
                  }}
                />

                {definition.work_area_id ? (
                  <Chip
                    size="small"
                    icon={<WorkspacesRounded />}
                    label={
                      workAreaNameById.get(
                        definition.work_area_id,
                      )
                      ?? 'Área no disponible'
                    }
                    sx={{
                      color: '#bae6fd',
                      bgcolor: alpha(
                        '#0284c7',
                        0.16,
                      ),
                    }}
                  />
                ) : null}

                {!definition.is_active ? (
                  <Chip
                    size="small"
                    label="Inactivo"
                    sx={{
                      color: '#cbd5e1',
                      bgcolor: alpha(
                        '#64748b',
                        0.12,
                      ),
                    }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="flex-end"
          >
            <IconButton
              size="small"
              aria-label="Editar parte"
              onClick={(event) => {
                event.stopPropagation();
                handleEditDefinition(definition);
              }}
              sx={{ color: '#94a3b8' }}
            >
              <EditRounded fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              aria-label="Eliminar parte"
              onClick={(event) => {
                event.stopPropagation();
                setDefinitionPendingDelete(
                  definition,
                );
              }}
              sx={{ color: '#fca5a5' }}
            >
              <DeleteRounded fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Paper>
    );
  };

  const renderField = (
    field: IndustrialWorkflowWorkReportFieldDefinition,
  ) => (
    <Paper
      key={field.id}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(
          field.is_active
            ? '#38bdf8'
            : '#64748b',
          0.16,
        )}`,
        bgcolor: 'rgba(15,23,42,0.34)',
        px: 1.25,
        py: 1.1,
      }}
    >
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        justifyContent="space-between"
        spacing={1}
      >
        <Stack
          direction="row"
          spacing={0.9}
          alignItems="flex-start"
        >
          {field.field_source === 'case_field' ? (
            <LinkRounded
              sx={{
                mt: 0.1,
                color: '#7dd3fc',
              }}
            />
          ) : (
            <TuneRounded
              sx={{
                mt: 0.1,
                color: '#c4b5fd',
              }}
            />
          )}

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
              }}
            >
              {field.label}
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.78rem',
                mt: 0.15,
              }}
            >
              {field.field_source === 'case_field'
                ? getCaseFieldWriteMode(field.settings)
                  === 'on_complete'
                  ? 'Dato del expediente que se puede completar o corregir desde este parte'
                  : 'Dato de contexto tomado del expediente'
                : 'Dato propio que se rellena en este parte'}
            </Typography>

            <Stack
              direction="row"
              spacing={0.55}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.7 }}
            >
              <Chip
                size="small"
                label={getFieldTypeLabel(field.field_type)}
                sx={{
                  color: '#cbd5e1',
                  bgcolor: alpha('#64748b', 0.12),
                }}
              />

              {field.is_required_to_complete ? (
                <Chip
                  size="small"
                  label="Obligatorio al completar"
                  sx={{
                    color: '#fde68a',
                    bgcolor: alpha('#f59e0b', 0.15),
                  }}
                />
              ) : null}

              {field.is_readonly ? (
                <Chip
                  size="small"
                  label="Solo lectura"
                  sx={{
                    color: '#bfdbfe',
                    bgcolor: alpha('#3b82f6', 0.15),
                  }}
                />
              ) : null}

              {field.field_source === 'case_field'
              && getCaseFieldWriteMode(field.settings)
                === 'on_complete' ? (
                <Chip
                  size="small"
                  label="Actualiza expediente al completar"
                  sx={{
                    color: '#bbf7d0',
                    bgcolor: alpha('#22c55e', 0.15),
                  }}
                />
              ) : null}

              {!field.is_active ? (
                <Chip
                  size="small"
                  label="Inactivo"
                  sx={{
                    color: '#cbd5e1',
                    bgcolor: alpha('#64748b', 0.12),
                  }}
                />
              ) : null}
            </Stack>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          justifyContent="flex-end"
        >
          <IconButton
            size="small"
            aria-label="Editar campo"
            onClick={() => handleEditField(field)}
            sx={{ color: '#94a3b8' }}
          >
            <EditRounded fontSize="small" />
          </IconButton>

          <IconButton
            size="small"
            aria-label="Eliminar campo"
            onClick={() =>
              setFieldPendingDelete(field)
            }
            sx={{ color: '#fca5a5' }}
          >
            <DeleteRounded fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${alpha(
          '#c4b5fd',
          0.22,
        )}`,
        bgcolor: 'rgba(15,23,42,0.66)',
        px: {
          xs: 1.6,
          md: 2,
        },
        py: 1.7,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            md: 'center',
          }}
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <FactCheckRounded
              sx={{
                color: '#c4b5fd',
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontWeight: 950,
                }}
              >
                Partes de trabajo
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.82rem',
                  mt: 0.2,
                }}
              >
                Define los registros estructurados que deberán completarse
                dentro de esta etapa.
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRounded />}
            onClick={handleCreateDefinition}
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              whiteSpace: 'nowrap',
            }}
          >
            Nuevo parte
          </Button>
        </Stack>

        {definitionsQuery.isError ? (
          <Alert severity="error" variant="outlined">
            {getErrorMessage(definitionsQuery.error)}
          </Alert>
        ) : null}

        {definitionsQuery.isLoading ? (
          <Typography sx={{ color: '#94a3b8' }}>
            Cargando partes de trabajo...
          </Typography>
        ) : null}

        {!definitionsQuery.isLoading
        && !definitionsQuery.isError
        && !orderedDefinitions.length ? (
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              bgcolor: 'rgba(14,116,144,0.08)',
            }}
          >
            Todavía no hay partes configurados para esta etapa.
            Añade el primero si esta fase necesita registrar datos
            estructurados del trabajo realizado.
          </Alert>
        ) : null}

        {orderedDefinitions.length ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'minmax(0, 0.95fr) minmax(0, 1.05fr)',
              },
              gap: 1.4,
            }}
          >
            <Stack spacing={0.9}>
              {orderedDefinitions.map(renderDefinition)}
            </Stack>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(
                  '#94a3b8',
                  0.14,
                )}`,
                bgcolor: 'rgba(2,6,23,0.2)',
                p: 1.35,
              }}
            >
              {selectedDefinition ? (
                <Stack spacing={1.2}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'row',
                    }}
                    justifyContent="space-between"
                    alignItems={{
                      xs: 'flex-start',
                      sm: 'center',
                    }}
                    spacing={1}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 900,
                        }}
                      >
                        Campos de {selectedDefinition.name}
                      </Typography>

                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.78rem',
                          mt: 0.15,
                        }}
                      >
                        Combina datos del expediente con campos propios
                        del parte.
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={0.7}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LinkRounded />}
                        onClick={handleCreateCaseField}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 850,
                        }}
                      >
                        Dato del expediente
                      </Button>

                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddRounded />}
                        onClick={handleCreateOwnField}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 900,
                        }}
                      >
                        Campo propio
                      </Button>
                    </Stack>
                  </Stack>

                  {fieldsQuery.isError ? (
                    <Alert severity="error" variant="outlined">
                      {getErrorMessage(fieldsQuery.error)}
                    </Alert>
                  ) : null}

                  {fieldsQuery.isLoading ? (
                    <Typography sx={{ color: '#94a3b8' }}>
                      Cargando campos...
                    </Typography>
                  ) : null}

                  {!fieldsQuery.isLoading
                  && !fieldsQuery.isError
                  && !orderedFields.length ? (
                    <Alert severity="info" variant="outlined">
                      Este parte todavía no tiene campos.
                    </Alert>
                  ) : null}

                  <Stack spacing={0.85}>
                    {orderedFields.map(renderField)}
                  </Stack>
                </Stack>
              ) : (
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{
                    bgcolor: 'rgba(14,116,144,0.08)',
                  }}
                >
                  Selecciona un parte para configurar sus campos.
                </Alert>
              )}
            </Paper>
          </Box>
        ) : null}
      </Stack>

      <Dialog
        open={definitionDialogOpen}
        onClose={handleCloseDefinitionDialog}
        fullWidth
        maxWidth="md"
        BackdropProps={WORK_REPORT_DIALOG_BACKDROP_PROPS}
        PaperProps={WORK_REPORT_DIALOG_PAPER_PROPS}
      >
        <DialogTitle>
          {editingDefinition
            ? 'Editar parte de trabajo'
            : 'Nuevo parte de trabajo'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Nombre del parte"
              value={definitionForm.name}
              onChange={(event) =>
                setDefinitionForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              fullWidth
              required
            />

            <TextField
              label="Descripción"
              value={definitionForm.description}
              onChange={(event) =>
                setDefinitionForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              multiline
              minRows={3}
              fullWidth
            />

            <TextField
              select
              label="Área responsable"
              value={definitionForm.work_area_id}
              onChange={(event) =>
                setDefinitionForm((current) => ({
                  ...current,
                  work_area_id: event.target.value,
                }))
              }
              fullWidth
            >
              <MenuItem value="">
                Sin área específica
              </MenuItem>

              {(workAreasQuery.data ?? []).map((area) => (
                <MenuItem key={area.id} value={area.id}>
                  {area.name}
                </MenuItem>
              ))}
            </TextField>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1.2,
              }}
            >
              <TextField
                select
                label="Cuándo se crea"
                value={definitionForm.creation_mode}
                onChange={(event) =>
                  setDefinitionForm((current) => ({
                    ...current,
                    creation_mode:
                      event.target.value as IndustrialWorkflowWorkReportCreationMode,
                  }))
                }
                fullWidth
              >
                <MenuItem value="auto_on_stage_entry">
                  Automáticamente al entrar en etapa
                </MenuItem>

                <MenuItem value="manual">
                  Manualmente cuando haga falta
                </MenuItem>
              </TextField>

              <TextField
                select
                label="Si el expediente vuelve a esta etapa"
                value={definitionForm.continuity_mode}
                onChange={(event) =>
                  setDefinitionForm((current) => ({
                    ...current,
                    continuity_mode:
                      event.target.value as IndustrialWorkflowWorkReportContinuityMode,
                  }))
                }
                fullWidth
              >
                <MenuItem value="always_new">
                  Crear siempre un parte nuevo
                </MenuItem>

                <MenuItem value="ask_on_reentry">
                  Preguntar si continuar uno anterior
                </MenuItem>

                <MenuItem value="continue_open_previous">
                  Continuar un parte abierto anterior
                </MenuItem>
              </TextField>
            </Box>

            <Stack spacing={0.4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      definitionForm.is_required_to_complete_stage
                    }
                    onChange={(event) =>
                      setDefinitionForm((current) => ({
                        ...current,
                        is_required_to_complete_stage:
                          event.target.checked,
                      }))
                    }
                  />
                }
                label="Este parte es obligatorio para avanzar de etapa"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={definitionForm.is_active}
                    onChange={(event) =>
                      setDefinitionForm((current) => ({
                        ...current,
                        is_active: event.target.checked,
                      }))
                    }
                  />
                }
                label="Parte activo"
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          {editingDefinition ? (
            <Button
              color="error"
              startIcon={<DeleteRounded />}
              disabled={definitionMutationIsPending}
              onClick={() =>
                setDefinitionPendingDelete(editingDefinition)
              }
            >
              Eliminar
            </Button>
          ) : null}

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={handleCloseDefinitionDialog}
            disabled={definitionMutationIsPending}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveDefinition}
            disabled={
              definitionMutationIsPending
              || !definitionForm.name.trim()
            }
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={fieldDialogOpen}
        onClose={handleCloseFieldDialog}
        fullWidth
        maxWidth="md"
        BackdropProps={WORK_REPORT_DIALOG_BACKDROP_PROPS}
        PaperProps={WORK_REPORT_DIALOG_PAPER_PROPS}
      >
        <DialogTitle>
          {editingField
            ? 'Editar campo del parte'
            : fieldForm.field_source === 'case_field'
              ? 'Añadir dato del expediente'
              : 'Nuevo campo propio del parte'}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label="Origen del campo"
              value={fieldForm.field_source}
              disabled={Boolean(editingField)}
              onChange={(event) =>
                setFieldForm((current) => ({
                  ...current,
                  field_source:
                    event.target.value as IndustrialWorkflowWorkReportFieldSource,
                  case_field_definition_id: '',
                  label: '',
                  code: '',
                  description: '',
                  case_field_write_mode: 'read_only',
                  is_readonly:
                    event.target.value === 'case_field',
                  options_text: '',
                  settings_text:
                    event.target.value === 'case_field'
                      ? JSON.stringify(
                          {
                            case_field_write_mode: 'read_only',
                          },
                          null,
                          2,
                        )
                      : '{}',
                }))
              }
              fullWidth
            >
              <MenuItem value="work_report_field">
                Campo propio del parte
              </MenuItem>

              <MenuItem value="case_field">
                Dato existente del expediente
              </MenuItem>
            </TextField>

            {fieldForm.field_source === 'case_field' ? (
              <TextField
                select
                label="Campo del expediente"
                value={fieldForm.case_field_definition_id}
                onChange={(event) => {
                  const selectedField =
                    caseFieldById.get(event.target.value);

                  setFieldForm((current) => ({
                    ...current,
                    case_field_definition_id:
                      event.target.value,
                    label: selectedField?.label ?? '',
                    code: selectedField?.code ?? '',
                    description:
                      selectedField?.description ?? '',
                    field_type:
                      selectedField?.field_type ?? 'text',
                    is_readonly:
                      current.case_field_write_mode
                      !== 'on_complete',
                    options_text:
                      selectedField?.options
                        ?.map((option) =>
                          typeof option === 'string'
                            ? option
                            : JSON.stringify(option),
                        )
                        .join('\n') ?? '',
                  }));
                }}
                fullWidth
                required
              >
                {(caseFieldsQuery.data ?? [])
                  .filter((field) => field.is_active)
                  .map((field) => (
                    <MenuItem
                      key={field.id}
                      value={field.id}
                    >
                      {field.label}
                    </MenuItem>
                  ))}
              </TextField>
            ) : null}

            {fieldForm.field_source === 'case_field' ? (
              <TextField
                select
                label="Uso del dato del expediente"
                value={fieldForm.case_field_write_mode}
                onChange={(event) => {
                  const nextMode =
                    event.target
                      .value as WorkReportCaseFieldWriteMode;

                  setFieldForm((current) => ({
                    ...current,
                    case_field_write_mode: nextMode,
                    is_readonly:
                      nextMode !== 'on_complete',
                  }));
                }}
                helperText={
                  fieldForm.case_field_write_mode === 'on_complete'
                    ? 'El valor se guardará como snapshot del parte y actualizará el expediente al completarlo.'
                    : 'El dato se mostrará como contexto y no podrá editarse desde este parte.'
                }
                fullWidth
              >
                <MenuItem value="read_only">
                  Mostrar como contexto
                </MenuItem>

                <MenuItem value="on_complete">
                  Completar/corregir desde este parte
                </MenuItem>
              </TextField>
            ) : null}

            <TextField
              label="Nombre visible"
              value={fieldForm.label}
              onChange={(event) =>
                setFieldForm((current) => ({
                  ...current,
                  label: event.target.value,
                }))
              }
              fullWidth
              required
            />

            <TextField
              label="Descripción"
              value={fieldForm.description}
              onChange={(event) =>
                setFieldForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              multiline
              minRows={2}
              fullWidth
            />

            <TextField
              select
              label="Tipo de dato"
              value={fieldForm.field_type}
              disabled={fieldForm.field_source === 'case_field'}
              onChange={(event) =>
                setFieldForm((current) => ({
                  ...current,
                  field_type:
                    event.target.value as IndustrialWorkflowFieldType,
                  options_text:
                    event.target.value === 'select'
                    || event.target.value === 'multi_select'
                      ? current.options_text
                      : '',
                }))
              }
              fullWidth
            >
              {FIELD_TYPES.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {fieldForm.field_source === 'work_report_field'
            && (
              fieldForm.field_type === 'select'
              || fieldForm.field_type === 'multi_select'
            ) ? (
              <TextField
                label="Opciones disponibles"
                value={fieldForm.options_text}
                onChange={(event) =>
                  setFieldForm((current) => ({
                    ...current,
                    options_text: event.target.value,
                  }))
                }
                helperText="Introduce una opción por línea."
                multiline
                minRows={4}
                fullWidth
              />
            ) : null}

            <Stack spacing={0.4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={
                      fieldForm.is_required_to_complete
                    }
                    onChange={(event) =>
                      setFieldForm((current) => ({
                        ...current,
                        is_required_to_complete:
                          event.target.checked,
                      }))
                    }
                  />
                }
                label="Obligatorio para completar el parte"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={fieldForm.is_readonly}
                    disabled={fieldForm.field_source === 'case_field'}
                    onChange={(event) =>
                      setFieldForm((current) => ({
                        ...current,
                        is_readonly: event.target.checked,
                      }))
                    }
                  />
                }
                label="Solo lectura"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={fieldForm.is_active}
                    onChange={(event) =>
                      setFieldForm((current) => ({
                        ...current,
                        is_active: event.target.checked,
                      }))
                    }
                  />
                }
                label="Campo activo"
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          {editingField ? (
            <Button
              color="error"
              startIcon={<DeleteRounded />}
              disabled={fieldMutationIsPending}
              onClick={() =>
                setFieldPendingDelete(editingField)
              }
            >
              Eliminar
            </Button>
          ) : null}

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={handleCloseFieldDialog}
            disabled={fieldMutationIsPending}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveField}
            disabled={
              fieldMutationIsPending
              || !(
                fieldForm.label.trim()
                || selectedCaseField?.label
              )
            }
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(definitionPendingDelete)}
        onClose={() => {
          if (!definitionMutationIsPending) {
            setDefinitionPendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        BackdropProps={WORK_REPORT_DIALOG_BACKDROP_PROPS}
        PaperProps={WORK_REPORT_DIALOG_PAPER_PROPS}
      >
        <DialogTitle>
          Eliminar parte configurado
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1}>
            <Typography>
              ¿Quieres eliminar este parte de trabajo?
            </Typography>

            <Alert severity="warning" variant="outlined">
              Solo se podrá eliminar si todavía no se ha usado en
              expedientes reales. Si ya tiene histórico, desactívalo.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDefinitionPendingDelete(null)}
            disabled={definitionMutationIsPending}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteDefinition}
            disabled={definitionMutationIsPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(fieldPendingDelete)}
        onClose={() => {
          if (!fieldMutationIsPending) {
            setFieldPendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        BackdropProps={WORK_REPORT_DIALOG_BACKDROP_PROPS}
        PaperProps={WORK_REPORT_DIALOG_PAPER_PROPS}
      >
        <DialogTitle>
          Eliminar campo del parte
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1}>
            <Typography>
              ¿Quieres eliminar este campo del parte?
            </Typography>

            <Alert severity="warning" variant="outlined">
              Solo se podrá eliminar si todavía no tiene valores en
              partes reales. Si ya tiene histórico, desactívalo.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setFieldPendingDelete(null)}
            disabled={fieldMutationIsPending}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteField}
            disabled={fieldMutationIsPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}